/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {Component, EventEmitter, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {extractValue} from '../../shared/utils/browser';
import * as moment from 'moment/moment';
import {forkJoin, from, merge, Observable, Subscription} from 'rxjs';
import {debounceTime, filter, finalize, mergeAll, switchMap, takeUntil} from 'rxjs/operators';

import {Destroyable} from '../../destroyable.component';
import {PayPeriodAttributes} from '../../models/models.interfaces';
import {AnyData, Data} from '../../shared/api';
import {RolesService} from '../../shared/authentication/roles/roles.service';
import {LabelledAndColoredDatum} from '../../shared/charts/d3/chart.interfaces';
import {EmployeesContextUtilsService} from '../../shared/employees/utils/employee-context-utils.service';
import {FiqlService} from '../../shared/fiql/fiql.service';
import {ModalService} from '../../shared/modal/modal.service';
import {CanComponentDeactivate} from '../../shared/routing/can-deactivate-guard';
import {DefaultPagination, Pagination} from '../../shared/ui/pagination.component';
import {TableComponent} from '../../shared/ui/table/table.component';
import {SortDirection} from '../../shared/ui/table/table.constants';
import {SortKey} from '../../shared/ui/table/table.interfaces';
import {User, UserService} from '../../shared/user/user.service';
import {Files} from '../../shared/utils/files';
import {createFiltersObj} from '../../shared/utils/payroll-filters';
import {OngoingPayrollsService} from '../ongoing/ongoing-payrolls.service';
import {PayTypeContextChange, PayTypeWithCliRegIds} from '../payrolls.model';
import {PayrollUtilsService} from '../utils/payroll-utils.service';

import {EventAction, PayEventsService} from './payroll-events.service';

@Component({
  selector: 'gp-view-calculation-events',
  templateUrl: './payroll-events.component.html',
  styleUrls: ['./payroll-events.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class CalculationEvents extends Destroyable implements CanComponentDeactivate, OnInit {

  public static ACCESS_NAME: string = 'CalculationEvents';

  public extractValue = extractValue;
  public selectedActions: Map<number, EventAction> = new Map<number, EventAction>();

  // Pagination state
  public pagination: Pagination = new DefaultPagination();

  public events: AnyData;
  public sortKey: SortKey = [];
  public dataLoaded: EventEmitter<void> = new EventEmitter<void>();

  // Stats
  public statistics: any;
  public payEventsHistoricChart: any;
  public payEventsStatusChart: any;
  public payEventsPerDomainChart: any;
  public payEventStatusHeaderChartData: Array<LabelledAndColoredDatum> = [];
  public payPeriods: Array<Data<PayPeriodAttributes>>;

  public allDoneEvents = [];
  public isAdministrator: boolean = false;
  @ViewChild(TableComponent, {static: true})
  public table: TableComponent;
  public filtersVisible: boolean;

  public filterFormGroup: UntypedFormGroup;
  public isPayTypeFilterDisabled: boolean = false;
  public isTodo: boolean = true;
  public menuOpened: boolean = false;
  public payTypes: PayTypeWithCliRegIds[] = [];
  public triggerContextPayType: Subscription;
  public triggerSortByTranslationPipe: boolean = true;
  public eventTypes = [
    {name: 'DROPDOWN.Error', key: 'ERROR_TECHNIC'},
    {name: 'DROPDOWN.Alert', key: 'ALERT'},
    {name: 'DROPDOWN.Warning', key: 'WARNING'},
    {name: 'DROPDOWN.Information', key: 'INFORMATION'}
  ];
  private user: User;
  private searchAttributes: any = {
    familyTransId: ''
  };
  private maxPerPageEvents: number = 20000;

  constructor(private payEventsService: PayEventsService,
              private fiql: FiqlService,
              private userService: UserService,
              private modalService: ModalService,
              public router: Router,
              private route: ActivatedRoute,
              private translate: TranslateService,
              private ongoingPayrollsService: OngoingPayrollsService,
              private employeesContextUtilsService: EmployeesContextUtilsService,
              private employeesContextService: EmployeesContextUtilsService,
              private rolesServie: RolesService,
              private payrollUtilsService: PayrollUtilsService,
              private formBuilder: UntypedFormBuilder) {
    super();
  }

  public ngOnInit() {
    const initialContextPayType = this.employeesContextService.getContextPayType();
    const initialQueryPayType: string = this.route.snapshot.queryParamMap.get('payTypeTransId');

    let payTypeTransId: string = '';
    if (initialContextPayType && (initialContextPayType.transId === '' || initialContextPayType.transId === initialQueryPayType)) {
      payTypeTransId = initialQueryPayType;
    } else if (initialContextPayType && initialContextPayType.transId) {
      payTypeTransId = initialContextPayType.transId;
      this.isPayTypeFilterDisabled = initialContextPayType.disablePayTypeFilter;
    }

    this.filterFormGroup = this.formBuilder.group({
      payTypeTransId: [payTypeTransId],
      name: [''],
      payroll: [''],
      eventType: ['']
    });

    this.fetchPayTypes();

    this.userService.getUser()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(user => this.user = user);

    this.route.queryParams
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((params: any) => {
        if (params.sort) {
          this.sortKey = [this.ongoingPayrollsService.createSortExpression(params.sort)];
        }

        this.filterFormGroup.setValue({
          payTypeTransId: params.payTypeTransId || this.filterFormGroup.get('payTypeTransId').value || '',
          payroll: params.payroll || '',
          name: params.name || '',
          eventType: params.eventType || ''
        });
        this.searchAttributes.familyTransId = params.familyTransId || '';

        this.updateCharts();
        this.fetchEvents();
        this.fetchPayTypes();
      });

    this.pagination.pageChanged
      .asObservable()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(this.fetchEvents.bind(this));

    this.ongoingPayrollsService.listOngoingPayPeriods()
      .subscribe(result => this.payPeriods = result);

    this.isAdministrator = this.rolesServie.hasRole('PAYROLL_ADMINISTRATOR');

    this.filterFormGroup.valueChanges
      .pipe(
        debounceTime(400),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.updateRoute();
      });

    this.translate.onLangChange.pipe(
      takeUntil(this.componentDestroyed$)
    )
      .subscribe(() => {
        this.triggerSortByTranslationPipe = !this.triggerSortByTranslationPipe;
      });
  }

  public updateRoute() {
    this.router.navigate(['payroll/calculation-events'], {
      queryParams: this.getRouteParams()
    });
  }

  public getRouteParams(): any {
    const params: any = createFiltersObj(this.filterFormGroup);

    const sortParam: string = this.getSortParam();
    if (sortParam !== '') {
      params.sort = sortParam;
    }

    return params;
  }

  public generateSearch(): string {
    let search: string = '(actif==true)';
    search += this.isTodo ? ';(status==todo)' : ';(status==done,status==done_byexception)';

    const payTypeTransId = this.filterFormGroup.get('payTypeTransId').value;
    if (payTypeTransId) {
      search += `;(payPeriodContract.payTypeTransId=='${payTypeTransId}')`;
    }

    const name = this.filterFormGroup.get('name').value;
    if (name) {
      const firstName: string =
        name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
      const lowerName: string = name.toLowerCase();
      const upperName: string = name.toUpperCase();
      search += `;(cliEmpfirstName=='*${lowerName}*',cliEmpfirstName=='${firstName}*',cliEmpLastName=='*${upperName}*')`;
    }

    const payroll = this.filterFormGroup.get('payroll').value;
    if (payroll) {
      const payrollArray = payroll.split(',');
      search += `;(payRollSerieName=='${payrollArray[0]}')`;
      search += payrollArray[1] ? `;(payRollPeriodsName=='${payrollArray[1]}')` : '';
    }

    const eventType = this.filterFormGroup.get('eventType').value;
    if (eventType) {
      if (Array.isArray(eventType)) {
        search += eventType.reduce((prev: string, curr: string, index: number) =>
          prev + `eventType==${curr}${index < eventType.length - 1 ? ',' : ''}`, ';(') + ')';
      } else {
        search += `;(eventType==${eventType})`;
      }
    }

    if (this.searchAttributes.familyTransId) {
      search += `;(familyTransId=='${this.searchAttributes.familyTransId}')`;
    }

    return search;
  }

  public exportToCSV(): void {
    const events: AnyData[] = [];
    let page = 0;
    let params: any = {
      perPage: this.maxPerPageEvents,
      page,
      search: this.generateSearch()
    };

    this.payEventsService.fetchPayEvents({params})
      .pipe(
        switchMap((response: any) => {
          const obsArr = [];

          while ((page + 1) * this.maxPerPageEvents < response.total) {
            page++;
            params = {
              perPage: this.maxPerPageEvents,
              page,
              search: this.generateSearch()
            };

            obsArr.push(this.payEventsService.fetchPayEvents({params}));
          }
          events.push(...response.payload);
          return merge(obsArr);
        }),
        mergeAll(),
        finalize(() => {
          events.forEach((event) => {
            event.payroll = `${event.payRollSerieName} - ${event.payRollPeriodsName}`;
            event.cliEmpName = `${event.cliEmpLastName} ${event.cliEmpfirstName}`;
            event.calculation = `${this.translate.instant('PAYROLL_EVENTS.n__', {
              eventAttributesPayExec: event.requestPerYear
            })} (${moment(event.payExecTimeStamp).format('MM/DD - HH:mm')})`;
            event.eventText = `${event.eventID || 0}.${this.translate.instant(event.transId, {args: event.args})}`;
          });

          Files.exportToCSV(events, [
            {name: this.translate.instant('COMMON.payroll'), field: 'payroll'},
            {name: this.translate.instant('FORMS.id'), field: 'contractID'},
            {name: this.translate.instant('FORMS.name'), field: 'cliEmpName'},
            {name: this.translate.instant('PAYROLL_EVENTS.calculation'), field: 'calculation'},
            {name: this.translate.instant('PAYROLL_RESULT.rule'), field: 'rule'},
            {name: this.translate.instant('PAYROLL_EVENTS.event'), field: 'eventText'}
          ], 'export', this.userService);
        })
      )
      .subscribe((response: any) => {
        events.push(...response.payload);
      });
  }

  public fetchEvents() {
    const params: any = {
      perPage: this.pagination.perPage,
      page: this.pagination.currentPage.number,
      search: this.generateSearch()
    };

    const orderBy: string = this.getSortParam();
    if (orderBy !== '') {
      params.orderBy = orderBy;
    }

    window.scroll(0, 0);
    this.payEventsService.fetchPayEvents({params})
      .pipe(
        finalize(() => {
          if (this.events === null) {
            this.events = [];
          }
          this.dataLoaded.emit();
        })
      )
      .subscribe(result => {
        this.events = result.payload;
        this.pagination.update(result.total);
        this.updateCharts();
        this.selectDoneEvents();
      });
  }

  public onSortKeyChange(key: SortKey) {
    this.sortKey = key;
    this.updateRoute();
  }

  public toggleSeeDone() {
    this.isTodo = !this.isTodo;
    this.fetchEvents();
  }

  public selectForDone(event: any, actionType: string) {
    if (event.employeeUnit === 'all') {
      const params: any = {
        id: event.id,
        transId: event.transId,
        nameEvent: event.nameEvent
      };

      switch (event.timeUnit) {
        case 'forever':
          break;
        case 'period':
          params.payRollPeriodsName = event.payRollPeriodsName;
          params.payRollSerieName = event.payRollSerieName;
          break;
        default:
          params.payExec = event.payExecTimeStamp;
          params.requestPerYear = event.requestPerYear;
          break;
      }
      this.addAllDoneEvent(params);
    }

    const eventAction: EventAction = {
      payEventResultID: event.id,
      actionType: actionType
    };

    if (actionType === 'DONE') {
      eventAction.disableType = this.getDisableType(event);
    }

    this.selectedActions.set(event.id, eventAction);
    event.dropdownOpen = false;
  }

  public openEvent(event: AnyData) {
    const params: any = {
      tab: 'Payroll result'
    };

    if (event.rule === 'PAYSLIP') {
      params.tab = 'Payslip';
    }

    params.line = event.namelink || event.ruleName;

    if (!event.contractID) {
      this.modalService.alert({
        title: 'ERROR.redirect_failed.title',
        text: 'ERROR.redirect_failed.text'
      });
    } else {
      this.ongoingPayrollsService.updateLastExecutionId(event.payExec);
      this.router.navigate(['payroll/analyze', event.contractID, params]);
    }
  }

  public canDeactivate(): boolean | Observable<boolean> {
    if (this.selectedActions.size === 0) {
      return true;
    }

    return from(
      this.modalService.confirm({
        'title': 'DIALOG.сancel_pending_selection',
        'text': 'DIALOG.cancel_current_selection?',
        'type': 'warning'
      })
    );
  }

  public toggleDropdown(event: any, dropdown: any, tableBody: any) {
    dropdown.style.visibility = 'hidden';
    dropdown.style.top = '';
    dropdown.style.position = 'absolute';

    event.dropdownOpen = !event.dropdownOpen;
    setTimeout(() => {
      const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
      const dropdownRect = dropdown.getBoundingClientRect();
      if (dropdownRect.top + dropdownRect.height > viewportHeight
        || tableBody.getBoundingClientRect().height !== tableBody.scrollHeight) {
        dropdown.style.top = `${-dropdownRect.height - 5}px`;
      }
      dropdown.style.visibility = '';
    });
  }

  public clear() {
    this.allDoneEvents = [];
    this.selectedActions.clear();
  }

  public executeSelectedActions() {
    this.events.forEach((event) => {
      if (!this.selectedActions.get(event.id)) {
        this.selectedActions.delete(event.id);
      }
    });

    this.payEventsService.setDonePayEvents(Array.from(this.selectedActions.values()))
      .subscribe(() => {
        this.events = null;
        this.selectedActions.clear();
        this.fetchEvents();
      });
  }

  private updateCharts() {

    forkJoin(
      this.payEventsService.getEventStatusChartAndStatistics(),
      this.payEventsService.getEventHistoricChartData(),
      this.payEventsService.getEventsPerDomainChart()
    ).subscribe(([payEventStatusChartAndStats,
                   payEventsHistoricChart,
                   payEventsPerDomainChart]: Array<any>) => {

      this.payEventsStatusChart = payEventStatusChartAndStats.chart;
      this.statistics = payEventStatusChartAndStats.statistics;
      this.payEventStatusHeaderChartData = [
        {
          label: this.translate.instant('DROPDOWN.Alert'),
          value: this.statistics.countsByType.alert, color: '#f44455'
        },
        {
          label: this.translate.instant('DROPDOWN.Error'),
          value: this.statistics.countsByType.error, color: '#000000'
        },
        {
          label: this.translate.instant('DROPDOWN.Warning'),
          value: this.statistics.countsByType.warning, color: '#fcc100'
        },
        {
          label: this.translate.instant('DROPDOWN.Information'),
          value: this.statistics.countsByType.information, color: '#6cc788'
        }
      ];
      this.payEventsHistoricChart = payEventsHistoricChart;
      this.payEventsPerDomainChart = payEventsPerDomainChart;
    });

  }

  private getDisableType(event): string {
    let disableType: string = event.employeeUnit === 'all' ? 'ALL' : '';

    switch (event.timeUnit) {
      case 'forever':
        disableType += 'EMPLOYEE_ALLTIME';
        break;
      case 'period':
        disableType += 'EMPLOYEE_PERIOD';
        break;
      default:
        disableType += 'EMPLOYEE_CALCULATION';
        break;
    }

    return disableType;
  }

  private addAllDoneEvent(value) {
    this.allDoneEvents.push(value);
    this.selectDoneEvents();
  }

  public deleteEvent(id) {
    this.selectedActions.delete(id);

    const allDoneEventIndex = this.allDoneEvents.findIndex((value: any) => value.id === id);

    if (allDoneEventIndex === -1) {
      return;
    }

    const allDoneEvent = this.allDoneEvents[allDoneEventIndex];
    this.allDoneEvents.splice(allDoneEventIndex, 1);

    this.events.forEach((event) => {
      if (!this.selectedActions.get(event.id) && event.nameEvent === allDoneEvent.nameEvent) {
        if (!allDoneEvent.payRollPeriodsName && !allDoneEvent.payExec) {
          this.selectedActions.delete(event.id);
        } else if (
          allDoneEvent.payRollPeriodsName === event.payRollPeriodsName &&
          allDoneEvent.payRollSerieName === event.payRollSerieName) {
          this.selectedActions.delete(event.id);
        } else if (moment(allDoneEvent.payExec).isSame(event.payExecTimeStamp, 'minute')
          && allDoneEvent.requestPerYear === event.requestPerYear) {
          this.selectedActions.delete(event.id);
        }
      }
    });
  }

  private selectDoneEvents() {
    this.events.forEach((event) => {
      this.allDoneEvents.forEach((element) => {
        if (event.id !== element.id && event.nameEvent === element.nameEvent) {
          if (!element.payRollPeriodsName && !element.payExec) {
            this.selectedActions.set(event.id);
          } else if (
            element.payRollPeriodsName === event.payRollPeriodsName &&
            element.payRollSerieName === event.payRollSerieName) {
            this.selectedActions.set(event.id);
          } else if (moment(element.payExec).isSame(event.payExecTimeStamp, 'minute')
            && element.requestPerYear === event.requestPerYear) {
            this.selectedActions.set(event.id);
          }
        }
      });
    });
  }

  private getSortParam() {
    return this.sortKey.map(element => {
      return (element.direction === SortDirection.DESCENDING ? '-' : '') + element.key;
    }).join(',');
  }

  private fetchPayTypes() {
    this.payrollUtilsService.payTypesSorted$
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter(response => response !== null)
      )
      .subscribe((response: PayTypeWithCliRegIds[]) => {
          this.payTypes = response;

          if (!this.triggerContextPayType) {
            this.listenContextPayType();
          }
        }
      );
  }

  private listenContextPayType() {
    this.triggerContextPayType = this.employeesContextUtilsService.listenContextPayType()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((newPayType: PayTypeContextChange) => {
        this.isPayTypeFilterDisabled = newPayType.disablePayTypeFilter;
        this.filterFormGroup.patchValue({payTypeTransId: newPayType.transId});
      });
  }

}
